import * as React from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

interface JsonViewerProps extends React.HTMLAttributes<HTMLPreElement> {
  /** The data to display as JSON */
  data: unknown;
  /** Whether to show line numbers */
  lineNumbers?: boolean;
  /** Maximum height before scrolling */
  maxHeight?: string;
}

/**
 * Component to display formatted JSON data with syntax highlighting.
 */
export function JsonViewer({
  data,
  lineNumbers = false,
  maxHeight = '400px',
  className,
  ...props
}: JsonViewerProps) {
  const [copied, setCopied] = React.useState(false);
  const jsonString = React.useMemo(() => JSON.stringify(data, null, 2), [data]);
  const lines = React.useMemo(() => jsonString.split('\n'), [jsonString]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  return (
    <div className="relative group">
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleCopy}
        aria-label={copied ? 'Kopiert' : 'In Zwischenablage kopieren'}
      >
        {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
      </Button>
      <pre
        className={cn(
          'overflow-auto rounded-lg border bg-muted/50 p-4 text-sm font-mono',
          className
        )}
        style={{ maxHeight }}
        {...props}
      >
        {lineNumbers ? (
          <code className="grid" style={{ gridTemplateColumns: 'auto 1fr' }}>
            {lines.map((line, index) => (
              <React.Fragment key={index}>
                <span className="select-none pr-4 text-right text-muted-foreground">
                  {index + 1}
                </span>
                <span>{highlightJson(line)}</span>
              </React.Fragment>
            ))}
          </code>
        ) : (
          <code>{highlightJson(jsonString)}</code>
        )}
      </pre>
    </div>
  );
}

/**
 * Basic JSON syntax highlighting.
 */
function highlightJson(text: string): React.ReactNode {
  // Match JSON tokens
  const tokenRegex =
    /("(?:[^"\\]|\\.)*")\s*:|("(?:[^"\\]|\\.)*")|(\b(?:true|false|null)\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenRegex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const [fullMatch, key, string, boolean, number] = match;

    if (key) {
      // Property key
      parts.push(
        <span key={match.index} className="text-json-key">
          {key.slice(0, -1)}
        </span>,
        ':'
      );
    } else if (string) {
      // String value
      parts.push(
        <span key={match.index} className="text-json-string">
          {string}
        </span>
      );
    } else if (boolean) {
      // Boolean or null
      parts.push(
        <span key={match.index} className="text-json-boolean">
          {boolean}
        </span>
      );
    } else if (number) {
      // Number
      parts.push(
        <span key={match.index} className="text-json-number">
          {number}
        </span>
      );
    } else {
      parts.push(fullMatch);
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}
